import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { Profile } from '../../types';
import { departamentosDaPessoa, rotuloFuncaoComercial } from '../lib/departamentos';

interface PersonPickerProps {
  /** id do profile selecionado; '' = opção padrão (eu mesmo) */
  value: string;
  onChange: (id: string) => void;
  profiles: Profile[];
  currentUserProfile: Profile | null;
  /** Rótulo da opção padrão, ex.: 'Minhas rotas', 'Importar para mim' */
  defaultLabel: string;
  className?: string;
  title?: string;
  /** Lado de ancoragem do dropdown no desktop (use 'right' quando o pill está na borda direita) */
  align?: 'left' | 'right';
}

/**
 * Seleção única de pessoa com o mesmo visual do ResponsibleFilter
 * (pill + dropdown agrupado por departamento com flyout). Clicar seleciona e fecha.
 */
const PersonPicker: React.FC<PersonPickerProps> = ({
  value,
  onChange,
  profiles,
  currentUserProfile,
  defaultLabel,
  className = '',
  title,
  align = 'left',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedDept, setExpandedDept] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setExpandedDept(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredProfiles = profiles.filter(p => {
    if ((p as any).status === 'inactive') return false;
    if (currentUserProfile && String(p.id) === String(currentUserProfile.id)) return false;
    return true;
  });

  const grouped: Record<string, typeof filteredProfiles> = {};
  filteredProfiles.forEach(p => {
    // Nomes antigos do Comercial (Interno/Externo) já saem como "Comercial".
    const proprios = departamentosDaPessoa(p);
    const depts = proprios.length > 0 ? proprios : ['Sem departamento'];
    depts.forEach((dept: string) => {
      if (!grouped[dept]) grouped[dept] = [];
      if (!grouped[dept].find(x => x.id === p.id)) grouped[dept].push(p);
    });
  });

  const deptKeys = Object.keys(grouped).sort((a, b) => {
    if (a === 'Sem departamento') return 1;
    if (b === 'Sem departamento') return -1;
    return a.localeCompare(b);
  });

  const selected = value ? profiles.find(p => String(p.id) === String(value)) : null;
  const pillLabel = selected ? (selected.full_name || '—') : defaultLabel;
  const avatarChar = (selected?.full_name || defaultLabel).charAt(0).toUpperCase();

  const pick = (id: string) => {
    onChange(id);
    setIsOpen(false);
    setExpandedDept(null);
  };

  return (
    <div className={`relative w-full md:w-auto ${className}`} ref={rootRef} title={title}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2.5 md:py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full shadow-sm hover:border-gray-300 dark:hover:border-gray-500 transition-all w-full md:w-auto justify-between group
        ${isOpen ? 'ring-2 ring-primary/10 border-primary' : ''}
        `}
      >
        <div className="flex items-center gap-2">
          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 transition-colors ${selected
            ? 'bg-primary/10 text-primary'
            : 'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-300 group-hover:bg-primary/10 group-hover:text-primary'
            }`}>
            {avatarChar}
          </div>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate max-w-[130px]">
            {pillLabel}
          </span>
        </div>
        <ChevronDown size={13} className={`text-gray-400 transition-transform duration-300 shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[60] bg-black/30 md:bg-transparent" onClick={() => { setIsOpen(false); setExpandedDept(null); }}></div>
          <div className={`fixed bottom-0 left-0 right-0 md:absolute md:bottom-auto md:top-full ${align === 'right' ? 'md:left-auto md:right-0' : 'md:left-0 md:right-auto'} mt-0 md:mt-2 w-full md:w-56 bg-white dark:bg-gray-800 rounded-t-2xl md:rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 z-[70] animate-fade-in md:overflow-visible flex flex-col max-h-[85vh] md:max-h-[60vh] pb-4 md:pb-2`}>
            <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto my-3 md:hidden flex-shrink-0"></div>
            <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 mb-2 md:hidden shrink-0">{defaultLabel}</p>

            {/* Opção padrão (eu mesmo) */}
            <div className="px-1.5 pb-1 pt-0 md:pt-1.5 flex-shrink-0">
              <button
                onClick={() => pick('')}
                className={`w-full flex items-center justify-between gap-3 px-3 py-3 md:py-2.5 rounded-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left ${value === '' ? 'bg-gray-50 dark:bg-gray-700' : ''}`}
              >
                <span className={`text-sm font-medium ${value === '' ? 'text-primary font-bold' : 'text-gray-700 dark:text-gray-200'}`}>{defaultLabel}</span>
                {value === '' && <Check size={14} className="text-primary shrink-0" />}
              </button>
              <div className="h-px bg-gray-100 dark:bg-gray-700 my-1 mx-2"></div>
            </div>

            {/* Departamentos */}
            <div className="flex-1 overflow-y-auto px-1.5 pb-2 custom-scrollbar md:pb-1.5 md:overflow-visible">
              {deptKeys.map(dept => {
                const isExpanded = expandedDept === dept;
                const hasSelected = !!grouped[dept].find(p => String(p.id) === String(value));

                return (
                  <div
                    key={dept}
                    className="relative"
                    onMouseEnter={() => setExpandedDept(dept)}
                    onMouseLeave={() => setExpandedDept(null)}
                  >
                    <button
                      onClick={() => setExpandedDept(isExpanded ? null : dept)}
                      className={`w-full text-left px-3 py-3 md:py-2.5 rounded-xl text-sm transition-all flex items-center justify-between gap-2
                          ${isExpanded
                          ? 'bg-primary/5 dark:bg-primary/10 text-primary font-bold'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium'
                        }
                      `}
                    >
                      <span className="truncate">{dept}</span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {hasSelected && (
                          <span className="px-1.5 py-0.5 text-[9px] font-bold bg-primary/10 text-primary rounded-full">✓</span>
                        )}
                        <ChevronDown size={12} className={`text-gray-400 md:block hidden ${align === 'right' ? 'rotate-90' : '-rotate-90'}`} />
                        <ChevronDown size={12} className={`text-gray-400 transition-transform md:hidden ${isExpanded ? 'rotate-180' : ''}`} />
                      </div>
                    </button>

                    {/* Desktop: flyout lateral (abre p/ dentro da tela) */}
                    {isExpanded && (
                      <div className={`hidden md:block absolute top-0 z-[80] ${align === 'right' ? 'right-full pr-1' : 'left-full pl-1'}`} style={{ pointerEvents: 'auto' }}>
                        <div className="w-64 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 p-1.5 max-h-[50vh] overflow-y-auto custom-scrollbar">
                          <p className="text-[10px] font-extrabold text-gray-400 dark:text-gray-500 uppercase tracking-widest px-3 pt-1 pb-1.5">{dept}</p>
                          {grouped[dept].sort((a, b) => a.full_name.localeCompare(b.full_name)).map(userProfile => {
                            const isSel = String(userProfile.id) === String(value);
                            return (
                              <button
                                key={userProfile.id}
                                onClick={() => pick(String(userProfile.id))}
                                className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left ${isSel ? 'bg-primary/5 dark:bg-primary/10' : ''}`}
                              >
                                <span className={`text-sm truncate ${isSel ? 'text-primary font-bold' : 'text-gray-700 dark:text-gray-200'}`}>{userProfile.full_name}{rotuloFuncaoComercial(userProfile) && <span className="ml-1 text-[11px] font-normal text-gray-400">· {rotuloFuncaoComercial(userProfile)}</span>}</span>
                                {isSel && <Check size={14} className="text-primary shrink-0" />}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Mobile: expansão inline */}
                    {isExpanded && (
                      <div className="md:hidden pl-4 border-l-2 border-primary/20 ml-3 my-1 space-y-0.5">
                        {grouped[dept].sort((a, b) => a.full_name.localeCompare(b.full_name)).map(userProfile => {
                          const isSel = String(userProfile.id) === String(value);
                          return (
                            <button
                              key={userProfile.id}
                              onClick={() => pick(String(userProfile.id))}
                              className={`w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left ${isSel ? 'bg-primary/5 dark:bg-primary/10' : ''}`}
                            >
                              <span className={`text-sm truncate ${isSel ? 'text-primary font-bold' : 'text-gray-700 dark:text-gray-200'}`}>{userProfile.full_name}{rotuloFuncaoComercial(userProfile) && <span className="ml-1 text-[11px] font-normal text-gray-400">· {rotuloFuncaoComercial(userProfile)}</span>}</span>
                              {isSel && <Check size={14} className="text-primary shrink-0" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PersonPicker;
